import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import type { AstrologerProfile } from '../../../types/professional';

export function AdminAstrologersPage() {
  const [astrologers, setAstrologers] = useState<AstrologerProfile[]>([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'suspended' | 'inactive'>('all');
  const [metrics, setMetrics]         = useState({ total: 0, active: 0, suspended: 0, totalClients: 0, totalAnalyses: 0 });

  useEffect(() => {
    supabase
      .from('astrologer_profiles')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        const list = (data ?? []) as AstrologerProfile[];
        setAstrologers(list);
        setMetrics({
          total:          list.length,
          active:         list.filter(a => a.is_active && !a.suspended_at).length,
          suspended:      list.filter(a => !!a.suspended_at).length,
          totalClients:   list.reduce((s, a) => s + (a.total_clients ?? 0), 0),
          totalAnalyses:  list.reduce((s, a) => s + (a.total_analyses ?? 0), 0),
        });
        setLoading(false);
      });
  }, []);

  async function toggleSuspend(ap: AstrologerProfile) {
    const isSuspended = !!ap.suspended_at;
    await supabase
      .from('astrologer_profiles')
      .update(isSuspended
        ? { suspended_at: null, suspended_reason: null }
        : { suspended_at: new Date().toISOString(), suspended_reason: 'Suspenso pelo admin' }
      )
      .eq('id', ap.id);
    setAstrologers(prev => prev.map(a =>
      a.id === ap.id
        ? { ...a, suspended_at: isSuspended ? null : new Date().toISOString() }
        : a
    ));
  }

  const filtered = astrologers.filter(a => {
    const q = search.toLowerCase();
    const matchSearch = !q || a.professional_name.toLowerCase().includes(q) || (a.contact_email ?? '').toLowerCase().includes(q);
    const matchStatus = filterStatus === 'all'
      || (filterStatus === 'active'    &&  a.is_active && !a.suspended_at)
      || (filterStatus === 'suspended' && !!a.suspended_at)
      || (filterStatus === 'inactive'  && !a.is_active);
    return matchSearch && matchStatus;
  });

  const cardStyle: React.CSSProperties = { background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '1.25rem' };

  return (
    <div>
      <h1 style={{ fontSize: 'clamp(1.25rem,3vw,1.5rem)', color: 'var(--text-primary)', margin: '0 0 1.5rem', fontFamily: 'var(--font-display)' }}>
        Astrólogos Pro
      </h1>

      {/* Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total', value: metrics.total },
          { label: 'Ativos', value: metrics.active, color: '#4ade80' },
          { label: 'Suspensos', value: metrics.suspended, color: '#f87171' },
          { label: 'Clientes', value: metrics.totalClients },
          { label: 'Análises', value: metrics.totalAnalyses },
        ].map(m => (
          <div key={m.label} style={cardStyle}>
            <p style={{ margin: '0 0 0.25rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{m.label}</p>
            <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: m.color ?? 'var(--petrol)', fontFamily: 'var(--font-display)' }}>{loading ? '…' : m.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <input
          className="form-input"
          style={{ flex: 1, minWidth: '200px' }}
          placeholder="Buscar por nome ou e-mail…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="form-input" style={{ width: 'auto' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value as typeof filterStatus)}>
          <option value="all">Todos os status</option>
          <option value="active">Ativos</option>
          <option value="suspended">Suspensos</option>
          <option value="inactive">Inativos</option>
        </select>
        <span style={{ alignSelf: 'center', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{filtered.length} astrólogo{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-muted)', padding: '2rem' }}>Carregando…</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>Nenhum resultado.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {filtered.map(ap => (
            <div key={ap.id} style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: '0 0 0.2rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
                  {ap.professional_name}
                  {ap.suspended_at && <span style={{ marginLeft: '0.5rem', fontSize: '11px', color: '#f87171', background: 'rgba(248,113,113,0.1)', padding: '2px 6px', borderRadius: '4px' }}>SUSPENSO</span>}
                  {!ap.is_active && !ap.suspended_at && <span style={{ marginLeft: '0.5rem', fontSize: '11px', color: 'var(--text-muted)', background: 'var(--bg-base)', padding: '2px 6px', borderRadius: '4px' }}>INATIVO</span>}
                </p>
                <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                  {ap.title && `${ap.title} · `}{ap.total_clients} clientes · {ap.total_analyses} análises
                  {ap.contact_email && ` · ${ap.contact_email}`}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap' }}>
                <Link to={`/admin-ap/astrologos/${ap.id}`} className="btn-secondary" style={{ fontSize: '12px', padding: '0.35rem 0.75rem' }}>
                  Ver perfil
                </Link>
                <button
                  className="btn-secondary"
                  style={{ fontSize: '12px', padding: '0.35rem 0.75rem', color: ap.suspended_at ? '#4ade80' : '#f87171', borderColor: ap.suspended_at ? '#4ade80' : '#f87171' }}
                  onClick={() => toggleSuspend(ap)}
                >
                  {ap.suspended_at ? 'Reativar' : 'Suspender'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
